import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import SocialAuthButtons from "@/components/auth/SocialAuthButtons";
import PasswordInput from "@/components/auth/PasswordInput";
import { toast } from "sonner";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ArrowLeft } from "lucide-react";

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

const Login = () => {
  const { t } = useTranslation();
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  // 2FA States
  const [show2FA, setShow2FA] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [factorId, setFactorId] = useState<string>("");

  useEffect(() => {
    // Only redirect if user is logged in AND not currently in 2FA flow
    if (user && !show2FA) {
      
      // Double check MFA status before redirecting blindly
       const checkMfa = async () => {
        const { data } = await supabase.auth.mfa.listFactors();
        const hasVerifiedFactor = data?.totp?.some(f => f.status === 'verified');
        
        if (hasVerifiedFactor) {
           const { data: levelData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
           if (levelData && levelData.currentLevel === 'aal1') {
             // User has 2FA but is only at level 1 -> needs verification
             // In this case, we stay on login page to prompt
             return; 
           }
        }
        
        navigate("/dashboard");
      };
      
      checkMfa();
    }
  }, [user, navigate, show2FA]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = loginSchema.safeParse({ email, password });
    if (!result.success) {
      const fieldErrors: { email?: string; password?: string } = {};
      result.error.errors.forEach((err) => {
        if (err.path[0] === "email") fieldErrors.email = t("auth.errors.invalidEmail");
        if (err.path[0] === "password") fieldErrors.password = t("auth.errors.passwordRequired");
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);
    const { error } = await signIn(email, password);
    
    if (error) {
      setLoading(false);
      if (error.message.includes("Invalid login credentials")) {
        toast.error(t("auth.errors.invalidCredentials"));
      } else if (error.message.includes("Email not confirmed")) {
        toast.error(t("auth.errors.emailNotConfirmed"));
      } else {
        toast.error(error.message);
      }
    } else {
      // Login successful, check for 2FA
      try {
        const { data, error: mfaError } = await supabase.auth.mfa.listFactors();
        if (mfaError) throw mfaError;

        const totpFactor = data.totp.find(f => f.status === 'verified');
        
        if (totpFactor) {
          // User has enabled 2FA
          setFactorId(totpFactor.id);
          setShow2FA(true);
          setLoading(false); // Stop loading, show 2FA input
          return;
        }

        // No 2FA, all good
        toast.success(t("auth.loginSuccess"));
        navigate("/dashboard");
        
      } catch (err) {
         console.error("MFA Check Error", err);
         // Fallback to simpler login if MFA check fails
         setLoading(false);
         navigate("/dashboard");
      }
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verifyCode.length !== 6) {
      toast.error(t('auth.twoFactor.codeRequired'));
      return;
    }

    setLoading(true);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verifyCode,
      });

      if (verifyError) throw verifyError;

      toast.success(t('auth.twoFactor.verifySuccess'));
      navigate("/dashboard");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : undefined;
      toast.error(message || t('auth.twoFactor.verifyFailed'));
      setLoading(false);
    }
  };

  if (show2FA) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-[hsl(var(--accent-pink))] rounded-2xl border-2 border-foreground p-8">
          <h1 className="heading-card text-center mb-6">{t('auth.twoFactor.title')}</h1>
          <p className="text-center text-sm mb-6 text-foreground/80">
            {t('auth.twoFactor.description')}
          </p>
          
          <form onSubmit={handleVerify2FA} className="space-y-4">
             <div>
              <input
                type="text"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                maxLength={6}
                className="w-full px-4 py-4 text-center text-2xl tracking-[0.5em] font-bold rounded-xl border-2 border-foreground bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="000000"
                autoFocus
              />
            </div>
            
            <button
              type="submit"
              disabled={loading || verifyCode.length !== 6}
              className="w-full btn-primary justify-center disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('auth.twoFactor.verify')}
            </button>
            
            <button
               type="button"
               onClick={() => window.location.reload()}
               className="w-full text-center text-sm hover:underline mt-4"
            >
              {t('auth.twoFactor.cancelAndReturn')}
            </button>
          </form>
        </div>
      </div>
    </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-[hsl(var(--accent-pink))] rounded-2xl border-2 border-foreground p-8">
          <Link to="/" className="logo block text-center mb-6">
            Vesper
          </Link>
          
          <h1 className="heading-card text-center mb-8">{t("auth.login")}</h1>

          <SocialAuthButtons />

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-foreground/30"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-[hsl(var(--accent-pink))] px-4 text-foreground/70">{t("auth.orContinueWith")}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t("auth.email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-full border-2 border-foreground bg-background text-foreground placeholder:text-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={t("auth.emailPlaceholder")}
              />
              {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
            </div>

            <PasswordInput
              label={t("auth.password")}
              value={password}
              onChange={setPassword}
              placeholder={t("auth.passwordPlaceholder")}
              error={errors.password}
            />

            <div className="text-right">
              <Link to="/forgot-password" className="text-sm font-medium hover:underline">
                {t("auth.forgotPassword")}
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary justify-center disabled:opacity-50 hover:bg-[hsl(var(--accent-cyan))]"
            >
              {loading ? t("auth.loading") : t("auth.login")}
            </button>
          </form>

          <p className="text-center mt-6 text-sm">
            {t("auth.noAccount")}{" "}
            <Link to="/signup" className="font-semibold hover:underline">
              {t("auth.signUp")}
            </Link>
          </p>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-foreground/70 hover:text-foreground inline-flex items-center gap-2 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              {t("auth.backToHome")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
